/**
 * Planeamento multimodal determinístico (módulo puro — sem IA, sem Places).
 *
 * Recebe as paragens já ordenadas de um dia e decide, por segmento, qual o
 * meio de transporte. Insere pontos explícitos de mudança de transporte
 * (estacionar, retomar veículo, levantar/devolver bicicleta ou trotinete) para
 * que nunca exista "teletransporte" de veículos.
 *
 * Regras:
 * - transporte principal entre zonas afastadas;
 * - caminhada dentro de grupos compactos;
 * - um modo só é usado quando melhora o percurso (nunca por estar disponível).
 */
import { haversineMeters } from "./geo";
import type { TransportMode } from "@/lib/transport-modes";

export type MultimodalTravel = {
  minutes: number;
  meters: number;
  label: string;
  mode?: TransportMode;
};

export type MultimodalStop = {
  kind: string;
  category?: string;
  name: string;
  time: string;
  durationMin: number;
  lat?: number;
  lng?: number;
  note?: string;
  travelFromPrev?: MultimodalTravel;
  [key: string]: unknown;
};

export type MultimodalPlan = {
  primary: TransportMode;
  secondary?: readonly TransportMode[];
  hybrid?: boolean;
};

/** Velocidades médias urbanas (km/h) — alinhadas com `pro/transport.ts`. */
const SPEED_KMH: Record<TransportMode, number> = {
  walk: 4.5,
  bike: 15,
  scooter: 15,
  motorcycle: 32,
  car: 30,
  transit: 18,
};

const LABEL: Record<TransportMode, string> = {
  walk: "a pé",
  bike: "de bicicleta",
  scooter: "de trotinete",
  motorcycle: "de mota",
  car: "de carro",
  transit: "de transportes públicos",
};

/** Distância máxima (m) em que cada modo é preferível ao transporte principal. */
const MAX_METERS: Partial<Record<TransportMode, number>> = {
  walk: 1200,
  scooter: 3000,
  bike: 6000,
};

/**
 * Distância mínima (m) para valer a pena levantar um veículo partilhado.
 * Abaixo disto caminha-se: levantar e devolver 5 minutos depois não faz sentido.
 */
const MIN_DOCKED_METERS: Partial<Record<TransportMode, number>> = {
  scooter: 1200,
  bike: 1500,
};

/** Minutos perdidos a levantar + devolver um veículo partilhado. */
const DOCKED_OVERHEAD_MINUTES = 10;

/** Modos que ficam estacionados e obrigam a regressar ao veículo. */
const PARKED_MODES: TransportMode[] = ["car", "motorcycle"];
/** Modos de aluguer partilhado: levantam-se e devolvem-se. */
const DOCKED_MODES: TransportMode[] = ["bike", "scooter"];


export function legLabel(mode: TransportMode): string {
  return LABEL[mode] ?? LABEL.walk;
}

export function legMinutes(meters: number, mode: TransportMode): number {
  const kmh = SPEED_KMH[mode] ?? SPEED_KMH.walk;
  return Math.max(1, Math.round((meters / 1000 / kmh) * 60));
}

/** Lista sem duplicados dos modos disponíveis para o dia. */
export function allowedModesOf(plan: MultimodalPlan): TransportMode[] {
  const out: TransportMode[] = [plan.primary];
  for (const mode of plan.secondary ?? []) if (!out.includes(mode)) out.push(mode);
  return out;
}

/** Só há planeamento multimodal quando existe mais do que um modo disponível. */
export function isMultimodalPlan(plan: MultimodalPlan): boolean {
  return allowedModesOf(plan).length > 1;
}

/**
 * Escolha determinística do modo de um segmento.
 * Distâncias curtas fazem-se sempre a pé; um veículo partilhado só entra
 * quando compensa mesmo o tempo de levantar e devolver.
 */
export function chooseSegmentMode(
  meters: number,
  plan: MultimodalPlan,
): TransportMode {
  const allowed = allowedModesOf(plan);
  const walkMinutes = legMinutes(meters, "walk");

  // Trajetos curtos: caminhar é sempre a melhor opção, mesmo que não esteja
  // listado como modo secundário.
  if (meters <= 900 && plan.primary !== "car" && plan.primary !== "motorcycle") {
    return "walk";
  }
  if (allowed.includes("walk") && meters <= (MAX_METERS.walk ?? 1200)) return "walk";

  for (const mode of ["scooter", "bike"] as TransportMode[]) {
    if (mode === plan.primary) continue;
    if (!allowed.includes(mode)) continue;
    const max = MAX_METERS[mode];
    if (max == null || meters > max) continue;
    if (meters < (MIN_DOCKED_METERS[mode] ?? 0)) continue;
    // Só compensa se poupar mais tempo do que o levantamento + devolução.
    if (walkMinutes - legMinutes(meters, mode) <= DOCKED_OVERHEAD_MINUTES) continue;
    return mode;
  }

  if (plan.primary === "walk") {
    // Caminhar é o principal: só troca para outro modo em distâncias longas.
    if (meters <= (MAX_METERS.walk ?? 1200)) return "walk";
    const upgrade = allowed.find((mode) => mode !== "walk");
    return upgrade ?? "walk";
  }
  return plan.primary;
}


type Point = { lat: number; lng: number };

const hasCoords = (stop: MultimodalStop): stop is MultimodalStop & Point =>
  typeof stop.lat === "number" && typeof stop.lng === "number";

function travel(from: Point, to: Point, mode: TransportMode): MultimodalTravel {
  const meters = Math.round(haversineMeters(from, to));
  return { meters, minutes: legMinutes(meters, mode), label: legLabel(mode), mode };
}

/**
 * Aplica o plano multimodal a um dia já ordenado.
 * Devolve as paragens com `travelFromPrev.mode` e com as mudanças de
 * transporte explícitas intercaladas.
 */
export function planMultimodalDay(
  stops: MultimodalStop[],
  plan: MultimodalPlan,
): MultimodalStop[] {
  if (!isMultimodalPlan(plan)) return stops;
  const geoIndexes = stops.map((stop, index) => (hasCoords(stop) ? index : -1)).filter((i) => i >= 0);
  if (geoIndexes.length < 2) return stops;

  // 1.ª passagem: modo de cada segmento (só depende da distância).
  const modes = new Map<number, TransportMode>();
  for (let k = 1; k < geoIndexes.length; k++) {
    const from = stops[geoIndexes[k - 1]] as MultimodalStop & Point;
    const to = stops[geoIndexes[k]] as MultimodalStop & Point;
    modes.set(geoIndexes[k], chooseSegmentMode(haversineMeters(from, to), plan));
  }

  // 2.ª passagem: continuidade dos veículos + pontos de mudança.
  const out: MultimodalStop[] = [];
  let position: Point | null = null;
  let parked: (Point & { mode: TransportMode; name: string }) | null = null;
  let docked: TransportMode | null = null;

  const closeDocked = (at: Point, time: string) => {
    if (!docked) return;
    out.push({
      kind: "transfer",
      category: "vehicle_return",
      name: docked === "bike" ? "🚲 Devolver a bicicleta" : "🛴 Devolver a trotinete",
      time,
      durationMin: 5,
      lat: at.lat,
      lng: at.lng,
      note: "Devolve o veículo antes de continuar noutro meio de transporte.",
    });
    docked = null;
  };

  for (let i = 0; i < stops.length; i++) {
    const stop = { ...stops[i] } as MultimodalStop;
    if (!hasCoords(stop)) {
      out.push(stop);
      continue;
    }
    const target: Point = { lat: stop.lat as number, lng: stop.lng as number };
    if (!position) {
      out.push(stop);
      position = target;
      continue;
    }
    const mode = modes.get(i) ?? plan.primary;
    const nextMode = (() => {
      const nextGeo = geoIndexes.find((index) => index > i);
      return nextGeo != null ? modes.get(nextGeo) : undefined;
    })();

    if (mode !== docked) closeDocked(position, stop.time);

    if (PARKED_MODES.includes(mode) && parked && parked.mode === mode) {
      const back = haversineMeters(position, parked);
      if (back > 30) {
        out.push({
          kind: "transfer",
          category: "vehicle_resume",
          name: `🔑 Regressar ao veículo — ${parked.name}`,
          time: stop.time,
          durationMin: 5,
          lat: parked.lat,
          lng: parked.lng,
          note: "Volta ao local onde deixaste o veículo antes da próxima deslocação.",
          travelFromPrev: travel(position, parked, "walk"),
        });
        position = { lat: parked.lat, lng: parked.lng };
      }
      parked = null;
    }

    if (DOCKED_MODES.includes(mode) && mode !== plan.primary && docked !== mode) {
      out.push({
        kind: "transfer",
        category: "vehicle_pickup",
        name: mode === "bike" ? "🚲 Levantar uma bicicleta" : "🛴 Levantar uma trotinete",
        time: stop.time,
        durationMin: 5,
        lat: position.lat,
        lng: position.lng,
        note: "Levantamento no ponto de partida desta deslocação.",
      });
      docked = mode;
    }

    if (PARKED_MODES.includes(mode) && nextMode === "walk") {
      // Chega de veículo e segue a pé: estaciona à chegada e caminha o resto.
      const parkStop: MultimodalStop = {
        kind: "parking",
        category: "parking",
        name: `🅿️ Estacionar ${mode === "car" ? "o carro" : "a mota"} — ${stop.name}`,
        time: stop.time,
        durationMin: 5,
        lat: target.lat,
        lng: target.lng,
        note: "As próximas paragens fazem-se a pé; o veículo fica aqui.",
        travelFromPrev: travel(position, target, mode),
      };
      out.push(parkStop);
      parked = { ...target, mode, name: parkStop.name };
      stop.travelFromPrev = { meters: 150, minutes: legMinutes(150, "walk"), label: legLabel("walk"), mode: "walk" };
      out.push(stop);
      position = target;
      continue;
    }

    stop.travelFromPrev = travel(position, target, mode);
    if (PARKED_MODES.includes(mode)) parked = null;
    out.push(stop);
    position = target;
  }
  if (position) closeDocked(position, out[out.length - 1]?.time ?? "");
  return out;
}

/** Segmentos consecutivos com o mesmo modo, para links de navegação. */
export type ModeSegment = {
  mode: TransportMode;
  label: string;
  stops: Array<{ lat: number; lng: number; name: string }>;
};

export function groupModeSegments(stops: MultimodalStop[]): ModeSegment[] {
  const segments: ModeSegment[] = [];
  let prev: (Point & { name: string }) | null = null;
  for (const stop of stops) {
    if (!hasCoords(stop)) continue;
    const point = { lat: stop.lat as number, lng: stop.lng as number, name: stop.name };
    const mode = stop.travelFromPrev?.mode;
    if (!prev || !mode) {
      prev = point;
      continue;
    }
    const last = segments[segments.length - 1];
    if (last && last.mode === mode) last.stops.push(point);
    else segments.push({ mode, label: legLabel(mode), stops: [prev, point] });
    prev = point;
  }
  return segments;
}
